import { Link } from "react-router-dom";
import {
  ArrowRight,
  Globe,
  Smartphone,
  Palette,
  Cloud,
  Zap,
  Shield,
} from "lucide-react";
import AnimateOnScroll, {
  StaggerContainer,
  StaggerItem,
} from "@/components/AnimateOnScroll";
import ServiceBackground from "@/components/ServiceBackground";
import { useTranslation } from "react-i18next";
import { services } from "@/lib/data";

const iconMap: Record<string, any> = { Globe, Smartphone, Palette, Cloud, Zap, Shield };

export default function LayananPage() {
  const { t } = useTranslation();

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        {/* Service background with buildings */}
        <ServiceBackground slug="web-development" />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 via-navy-950/40 to-navy-950" />

        <div className="section-container section-padding relative z-10">
          <AnimateOnScroll className="text-center max-w-3xl mx-auto">
            <div className="p-8 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-white/[0.06]">
              <span className="text-neon-400 font-mono text-sm tracking-widest uppercase">
                {t('home.layanan_page.kategori')}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-6 gradient-text-white">
                {t('home.layanan_page.title_main')} <span className="gradient-text">{t('home.layanan_page.title_highlight')}</span>
              </h1>
              <p className="text-white/50 text-lg leading-relaxed">
                {t('home.layanan_page.desc')}
              </p>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-24">
        <div className="section-container section-padding">
          <StaggerContainer className="space-y-8" staggerDelay={0.12}>
            {((t('home.layanan_services', { returnObjects: true }) || []) as any[]).map((service, index) => {
              const originalService = services[index] || services[0];
              const Icon = iconMap[originalService.icon];
              return (
                <StaggerItem key={originalService.slug}>
                  <Link to={`/layanan/${originalService.slug}`} className="block group">
                    <div className="glass-card-hover p-8 md:p-10">
                      <div className="grid md:grid-cols-[1fr_2fr] gap-8">
                        {/* Left */}
                        <div>
                          <div className="w-16 h-16 rounded-2xl bg-neon-500/10 flex items-center justify-center mb-5 group-hover:bg-neon-500/20 transition-colors">
                            {Icon && <Icon className="w-8 h-8 text-neon-400" />}
                          </div>
                          <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-neon-400 transition-colors">
                            {service.title}
                          </h2>
                          <p className="text-neon-400/60 text-sm font-medium mb-4">
                            {service.tagline}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {originalService.techStack.map((tech: string) => (
                              <span
                                key={tech}
                                className="px-2.5 py-1 text-xs font-mono bg-white/5 text-white/40 rounded-md"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                        {/* Right */}
                        <div>
                          <p className="text-white/50 leading-relaxed mb-6">
                            {service.description}
                          </p>
                          <div className="grid sm:grid-cols-2 gap-3 mb-6">
                            {service.subServices.map((sub: any, subIndex: number) => (
                              <div
                                key={subIndex}
                                className="p-3 bg-white/[0.02] rounded-lg border border-white/[0.04]"
                              >
                                <h4 className="text-sm font-medium text-white/80 mb-1">
                                  {sub.title}
                                </h4>
                                <p className="text-xs text-white/30 line-clamp-2">
                                  {sub.description}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center gap-2 text-neon-400 font-medium text-sm">
                            {t('home.layanan_page.pelajari_detail')}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32 pt-10 relative z-10">
        <div className="section-container section-padding text-center">
          <AnimateOnScroll>
            <h2 className="text-3xl md:text-4xl font-bold gradient-text-white mb-4">
              {t('home.layanan_cta.title')}
            </h2>
            <p className="text-white/50 max-w-xl mx-auto mb-8">
              {t('home.layanan_cta.desc')}
            </p>
            <Link to="/konsultasi" className="btn-primary">
              {t('home.layanan_cta.btn')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimateOnScroll>
        </div>
      </section>
    </>
  );
}
